import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Coffee, Heart, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { restaurantImage } from "@/constants/nossaData";
import { MenuPreviewCard } from "@/components/nossa/MenuPreviewCard";
import { ReviewsSection } from "@/components/nossa/ReviewsSection";

const featuredDishIds = ["huevos-rotos-jamon", "torrezno-soria", "nossa-burger"];

export default function Home({ brand, menu, reviews }) {
  const featured = useMemo(() => {
    const selected = featuredDishIds.map((id) => menu.find((item) => item.id === id)).filter(Boolean);
    return selected.length === featuredDishIds.length ? selected : menu.slice(0, 3);
  }, [menu]);

  return (
    <>
      <HeroSection brand={brand} />
      <AboutSection brand={brand} />
      <ReviewsSection reviews={reviews} />
      <FeaturedSection featured={featured} />
      <HomeReservationCta />
    </>
  );
}

function HeroSection({ brand }) {
  return (
    <section className="hero-section" data-testid="home-hero-section">
      <div className="hero-copy">
        <span className="eyebrow" data-testid="home-eyebrow">Bar · Café · Encuentro</span>
        <h1 data-testid="home-title">{brand.slogan}</h1>
        <p data-testid="home-purpose">{brand.purpose}</p>
        <div className="hero-actions">
          <Button asChild className="primary-cta" data-testid="home-menu-cta-button">
            <Link to="/menu">Ver menú <ArrowRight size={18} /></Link>
          </Button>
          <Button asChild variant="outline" className="secondary-cta" data-testid="home-contact-cta-button">
            <Link to="/contacto">Dónde estamos</Link>
          </Button>
        </div>
      </div>
      <div className="hero-card" data-testid="home-restaurant-image-card">
        <img src={restaurantImage} alt="Barra del restaurante Nossa" data-testid="home-restaurant-image" />
        <div className="hero-card-note" data-testid="home-card-note">
          <Coffee size={18} /> Una pausa cálida en cada visita
        </div>
      </div>
    </section>
  );
}

function AboutSection({ brand }) {
  return (
    <section className="about-section" data-testid="about-section">
      <div>
        <span className="eyebrow" data-testid="about-eyebrow">Sobre Nossa</span>
        <h2 data-testid="about-title">Un espacio para sentirse cerca.</h2>
      </div>
      <p data-testid="about-mission">{brand.mission}</p>
      <div className="values-grid" data-testid="brand-values-grid">
        {brand.values.map((value, index) => <ValueCard key={value} index={index} value={value} />)}
      </div>
    </section>
  );
}

function ValueCard({ value, index }) {
  return (
    <Card className="value-card" data-testid={`brand-value-card-${index}`}>
      <CardContent>
        <Heart size={20} />
        <h3 data-testid={`brand-value-title-${index}`}>{value}</h3>
        <p data-testid={`brand-value-description-${index}`}>{getValueDescription(value)}</p>
      </CardContent>
    </Card>
  );
}

function FeaturedSection({ featured }) {
  return (
    <section className="featured-section" data-testid="home-featured-menu-section">
      <div className="section-heading">
        <span className="eyebrow" data-testid="featured-eyebrow">Carta</span>
        <h2 data-testid="featured-title">Sabores para una pausa con calma.</h2>
      </div>
      <div className="featured-grid">
        {featured.map((item) => <MenuPreviewCard key={item.id} item={item} />)}
      </div>
    </section>
  );
}

function HomeReservationCta() {
  return (
    <section className="home-reservation-cta" data-testid="home-reservation-cta-section">
      <span className="eyebrow" data-testid="home-reservation-cta-eyebrow">Reserva</span>
      <h2 data-testid="home-reservation-cta-title">Reserva tu mesa</h2>
      <p data-testid="home-reservation-cta-description">Escríbenos por WhatsApp y preparamos tu pausa en Nossa.</p>
      <a className="home-reservation-button" href="https://wa.me/34680502341?text=Hola%20Nossa%2C%20quiero%20reservar%20una%20mesa" target="_blank" rel="noreferrer" data-testid="home-reservation-whatsapp-button">
        Reserva tu mesa <Phone size={18} />
      </a>
    </section>
  );
}

function getValueDescription(value) {
  if (value === "Cercanía") return "Un lugar que se siente propio, cómodo y sin pretensiones.";
  if (value === "Autenticidad") return "Una identidad honesta, simple y construida con intención.";
  return "Momentos que se recuerdan por cómo te hacen sentir.";
}